<template>
  <div class="list-wrapper" :class="{ 'has-load-more': showLoadMore, 'is-in-dropdown': isInDropdown, 'has-spacing-around': hasSpacingAround }">
    <b-field
      v-if="filterable"
      expanded
      
      :label="computedLabel"
      class="add-tags-filter-input"
    >
      <b-input
        autofocus
        v-model="text"
        @keyup.enter.native.prevent.stop="onEnter()"
        ref="input"
        autocapitalize="none"
        @keydown.native.left.exact.prevent.stop="onArrowLeft"
        @keydown.native.right.exact.prevent.stop="onArrowRight"
        @keydown.native.tab="onPressTab"
        class="has-border"
        placeholder="Type to filter..."
      ></b-input>
    </b-field>
    <b-taglist
      class="taglist"
      :class="{
        'is-scrollable': scrollable,
        'has-limited-height': limitHeight,
      }"
      ref="list"
    >
      <tag-item
        v-bind="tagProps"
        :tag="allTags[tag.id] || tag"
        @click.native="onTagClick(tag, $event)"
        v-for="(tag, index) in tagsToShow"
        :key="`${context}-tag-${tag.id}-${index}`"
        :class="{
          'is-keyboard-focused': (filterable || filterValue !== null) && keyboardIndex === index
        }"
        :is-in-dropdown="isInDropdown"
        @close="$emit('close', tag)"
        :context-options="contextOptions"
      ></tag-item>
      <b-tag
        v-if="showCreateTag"
        :type="keyboardIndex === 'CREATE' ? 'is-primary' : 'is-dark'"
        size="is-medium"
        class="has-pointer"
        @click.native="createTag"
      >
        <nice-icon icon="plus"></nice-icon>
        {{ $t("buttons.createThisTag") }}
      </b-tag>

      <slot></slot>

      <div class="flex justify-center w-100 mt1" v-if="showLoadMore">
        <b-button
          size="is-small" 
          @click.prevent.stop="showMore()"
        >{{ $t("buttons.showMoreTags") }}</b-button>        
      </div>
  
    </b-taglist>
    
    <small v-if="showTagCount" class="tag-count flex f6 has-text-light-30">Showing {{ tagsToShow.length }} / {{ allTagsArray.length }} Tags</small>

    <b-modal
      v-if="canShare && shareModalVisible"
      :active.sync="shareModalVisible"
      has-modal-card
      animation="pop-modal"
    >
      <div class="modal-card" style="width: 540px">
        <header class="modal-card-head">
          <p class="modal-card-title flex items-center">
            <span>{{ $t("buttons.share") }}</span>
            <tag-item
              :tag="currentTag"
              :is-in-dropdown="true"
              style="display: inline-flex; margin-bottom: 0;"
              class="ml3"
              :clickable="false"
            ></tag-item>
          </p>
        </header>
        <section class="modal-card-body">
          <b-message type="is-info" has-icon icon-size="is-medium">
            This tag is connected to {{ currentTag.resources_count }} resources.
            Everyone with the link can see every resource connected to this
            tag.
            <br />You don't need an account to view the link.
          </b-message>

          <heading size="small">Links</heading>

          <div class="is-muted" v-if="!tagLinksGrouped[currentTag.id] || tagLinksGrouped[currentTag.id].length < 1">No links created</div>

          <tag-link
            v-for="link in tagLinksGrouped[currentTag.id]"
            :key="`tag-link-${link.id}`"
            :link="link"
            class="mb3"
          ></tag-link>

          <div class="mt3">
            <!-- <div class="mb2">
              <nice-icon icon="eye" spacing-right></nice-icon>
              <div>
                <div>Read-only link</div>
                <div class="is-muted" v-if="!tagLinksGrouped[currentTag.id] || tagLinksGrouped[currentTag.id].length < 1">No links created</div>
                <div class="is-muted" v-else>{{ tagLinksGrouped[currentTag.id].length }}{{ tagLinksGrouped[currentTag.id].length > 1 ? "links" : "link"}} created</div>
              </div>
            </div> -->

            <heading size="tiny" spacing-bottom>Create new link</heading>

            <b-field>
              <b-input
                placeholder="Title (optional)"
                expanded
                class="stretch-input"
                v-model="tagLink.title"
                :disabled="$wait.is('creating link')"
                @keyup.enter.native.exact="createLink"
              ></b-input>
              <p class="control">
                <b-button
                  style="height:100%;"
                  @click="createLink()"
                  :loading="$wait.is('creating link')"
                >{{ $t("buttons.createLink") }}</b-button>
              </p>
            </b-field>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button
            class="button"
            type="button"
            @click="shareModalVisible = false"
          >{{ $t("buttons.close") }}</b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TagItem from "./Item.vue";
import TagLink from "@/shared/components/tag/Link.vue";

export default {
  props: {
    exclude: {
      type: Array
    },
    use: {
      type: Array
    },
    filterable: {
      type: Boolean,
      default: true
    },
    showAll: {
      type: Boolean,
      default: true
    },
    chunk: {
      type: Number,
      default: 10
    },
    scrollable: {
      type: Boolean,
      default: true
    },
    allTagsMap: {
      type: [Object, null],
      default: null
    },
    label: {
      type: String
    },
    isInDropdown: {
      type: Boolean,
      default: false
    },
    hasSpacingAround: {
      type: Boolean,
      default: false
    },
    limitHeight: {
      type: Boolean,
      default: true
    },
    canLoadMore: {
      type: Boolean,
      default: true
    },
    canShare: {
      type: Boolean,
      default: false
    },
    canRename: {
      type: Boolean,
      default: true
    },
    canDelete: {
      type: Boolean,
      default: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    tagProps: {
      type: Object,
      default: null
    },
    noContextMenu: {
      type: Boolean,
      default: false
    },
    contextOptions: {
      type: Object,
      default: () => ({})
    },
    showDefaultOptions: {
      type: Boolean,
      default: true
    },
    showTagCount: {
      type: Boolean,
      default: true
    },
    filterValue: {
      type: String,
      default: null
    },
    context: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      text: "",
      canCreateTag: true,
      isCreatingTag: false,
      activatedTag: null,
      showAllTags: false,
      page: 1,
      keyboardIndex: -1,
      shareModalVisible: false,
      currentTag: {},
      tagLink: {},
      editTagLink: null
    };
  },

  methods: {
    onEnter() {
      if (this.keyboardIndex === "CREATE") {
        this.createTag();
        return;
      }
      if (this.tagsFiltered[this.keyboardIndex]) {
        this.$emit("tagClicked", this.tagsFiltered[this.keyboardIndex]);
        this.text = "";
      } else {
        this.createTag();
      }
    },

    async createTag() {
      this.canCreateTag = false;

      this.$emit("beforeTagCreate", { text: this.text, isLoading: true })

      const text = this.text;
      this.text = "";

      this.canCreateTag = true;
      // this.isCreatingTag = false;
      this.keyboardIndex = -1;
      this.$refs.input.focus();

      if (this.$store) {
        const tag = await this.$store.dispatch(`${this.context}/createTag`, {
          text: text
        });
        if (tag) {
          this.$emit("tagClicked", tag);
        } else {
          this.$emit("errorTagCreate", { text: this.text })
          this.$buefy.snackbar.open({
            message: "Error while creating tag",
            type: "is-warning",
            position: "is-top",
            actionText: "Retry",
            duration: 3500,
            onAction: async () => {
              const tag = await this.$store.dispatch(`${this.context}/createTag`, {
                text: text
              });
              if (!tag) {
                this.$buefy.toast.open({
                  message: "Failed again. Please try again later.",
                  type: "is-danger"
                });
                this.text = text;
              }
            }
          });
        }
      } else {
        this.$emit("createTag", {
          text: text
        });
      }
    },

    onTagClick(tag, event) {
      this.$emit("tagClicked", tag, event);
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },

    showMore() {
      this.page++;
      this.$nextTick(() => {
        this.$refs.list.$el.scrollTop = this.$refs.list.$el.scrollHeight;
      });
    },

    focusInput() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },

    onPressTab(e) {
      if (this.tagsFilteredLimited.length > 0) {
        e.preventDefault();
        e.stopPropagation();

        this.onArrowRight();
      }
    },

    onArrowLeft() {
      if (this.keyboardIndex === "CREATE") {
        if (this.tagsFilteredLimited.length > 0) {
          this.keyboardIndex = this.tagsFilteredLimited.length - 1;
        }
        return false;
      }
      const newIndex = this.keyboardIndex - 1;
      if (this.tagsFilteredLimited[newIndex]) {
        this.keyboardIndex = newIndex;
      }
    },
    onArrowRight() {
      if (this.keyboardIndex === "CREATE") return false;
      const newIndex = this.keyboardIndex + 1;
      if (this.tagsFilteredLimited[newIndex]) {
        this.$set(this, 'keyboardIndex', newIndex)
      } else {
        if (this.showCreateTag) {
          this.keyboardIndex = "CREATE";
        }
      }
    },

    startRename(tag) {
      this.$buefy.dialog.prompt({
        message: `New name of the tag`,
        inputAttrs: {
          value: tag.text
        },
        animation: "pop-modal",
        onConfirm: value => {
          this.$store.dispatch(
            `${this.context}/updateTag`,
            Object.assign({}, tag, { text: value })
          );
          this.$buefy.toast.open({
            message: `Tag renamed`,
            type: "is-success"
          });
        }
      });
    },

    deleteTag(tag) {
      this.$store.dispatch(`${this.context}/deleteTag`, tag);
    },

    openShareModal(tag) {
      this.shareModalVisible = true;
      this.currentTag = tag;
    },

    async createLink() {
      this.$wait.start("creating link");
      await this.$store.dispatch("createTagLink", {
        ...this.tagLink,
        tag_id: this.currentTag.id
      });
      this.$wait.end("creating link");
    },

    increaseFocusIndex() {
      this.onArrowRight()
    },
    decreaseFocusIndex() {
      this.onArrowLeft()
    },
    getSelected() {
      return this.tagsFiltered[this.keyboardIndex]
    }
  },

  watch: {
    tagsFilteredLimited() {
      // if (val.length - 1 > this.keyboardIndex) {
      //   this.keyboardIndex = val.length - 1
      // }
      // if (val.length < 1 && this.showCreateTag) {
      //   this.keyboardIndex = "CREATE"
      // }
      this.keyboardIndex = -1;
    }
  },

  computed: {
    computedLabel() {
      return this.label || this.$t("fields.filterTags");
    },

    tagLinksGrouped() {
      return this.$store.getters.tagLinksGrouped;
    },

    showCreateTag() {
      return !this.hasExactTag && !this.isTextEmpty && this.canCreateTag;
    },

    tagsToShow() {
      return this.shouldShowAll
          ? this.tagsFiltered
          : this.tagsFilteredLimited
    },

    tagsFilteredLimited() {
      return this.tagsFiltered.slice(0, this.chunk * this.page);
    },

    tagsFiltered() {
      let textNormalized = ""

      if (this.filterable) {
        textNormalized = this.text.toLowerCase();
      } else if (this.filterValue) {
        textNormalized = this.filterValue.toLowerCase();
      }

      if (textNormalized.length < 2) {
        if (!this.exclude) return this.use;
        return this.use.filter(t => !this.exclude.includes(t.id));
      } else {
        return this.use.filter(
          t =>
            t.text && t.text.toLowerCase().includes(textNormalized) &&
            (!this.exclude || !this.excludeObject[t.id])
        );
      }
    },
    hasExactTag() {
      return this.text.length > 1 && !!this.use.find(t => t.text == this.text);
    },
    isTextEmpty() {
      return this.text.length < 1;
    },

    shouldShowAll() {
      return (
        this.showAll ||
        this.showAllTags ||
        this.tagsFiltered.length === this.chunk
      );
    },

    allTags() {
      return (this.$store && this.$store.getters[`${this.context}/tags`]) || this.allTagsMap;
    },
    allTagsArray() {
      return (this.$store && this.$store.getters[`${this.context}/tagsArray`]) || Object.values(this.allTagsMap);
    },

    isShowingAll() {
      return this.chunk * this.page > this.use.length;
    },
    isShowingAllFiltered() {
      return this.chunk * this.page > this.tagsFilteredLimited.length;
    },

    excludeObject() {
      let obj = {}

      for (const id of this.exclude) {
        obj[id] = true
      }

      return obj
    },

    showLoadMore() {
      return this.canLoadMore && !this.isShowingAll && !this.isShowingAllFiltered
    }
  },

  components: {
    TagItem,
    TagLink,
  }
};
</script>

<style lang="scss" scoped>

.list-wrapper {
  &.is-in-dropdown {
  
    .taglist {
      scrollbar-color: $light_10 $dropdown-content-background-color;

      &::-webkit-scrollbar-track {
        background: $dropdown-content-background-color;
      }
    }
  }

  &.has-spacing-around {
    .taglist {
      padding: 15px;
      padding-bottom: 0;
    }

    .tag-count {
      padding: 15px;
    }
  }
}

.add-tags-container {
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
}

.add-tags-button {
  color: $light_10;
  transition: 200ms all;

  &:hover {
    color: $text;
  }
}

.taglist {
  margin-bottom: 0;

  &.has-limited-height {
    max-height: 200px;
  }

  &.is-scrollable {
    overflow-y: auto;
  }
}

.tag-count {
  padding-top: 10px !important;
}

.small-button {
  padding-left: 10px !important;
  padding-right: 10px !important;
}
</style>
